import * as React from "react";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type {
  CPGridV2Column,
  CPGridV2FilterOption,
} from "shared/components/shared/CPGridV2";
import "./AdminDownloadsPageStyles.scss";
import { CPText, Link } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import { Chip } from "@progress/kendo-react-buttons";
import { GridFetchResponse, ValueObject } from "shared/types";
import DownloadDetailsModal, {
  UpsertDownloadRecord,
} from "./DownloadDetailsModal";
import { useQuery, useQueryClient } from "react-query";
import {
  useAgenciesLookupQuery,
  useResourceCategoriesLookupQuery,
} from "data/lookup/lookupQueries";
import http from "shared/http";
import useAppState from "shared/state/useAppState";
import { useConfirm } from "shared/components/shared/CPConfirmModal/useCPConfirmModalContext";
import { useDeleteDownloadMutation } from "data/resources/download";

interface UseGetDownloadsForGridQueryOptions {
  title?: string;
  category?: string;
  agency?: string;
  tier?: string;
  take?: number;
  skip?: number;
  sortBy?: string;
  ascending?: boolean;
}

const useGetDownloadsForGridQuery = (
  params: UseGetDownloadsForGridQueryOptions,
) => {
  return useQuery(
    ["downloads-grid", JSON.stringify(params)],
    async () => {
      const response: GridFetchResponse = await http.fetchAsync(
        "admin/downloads/search",
        params,
      );
      return response;
    },
    {
      staleTime: 30 * 1000,
      enabled: true,
    },
  );
};

// Columns
interface ColumnHandlers {
  onEdit: (download: UpsertDownloadRecord) => void;
  onDelete: (download: UpsertDownloadRecord) => void;
}

const createColumns = ({
  onEdit,
  onDelete,
}: ColumnHandlers): CPGridV2Column[] => [
  {
    columnType: "menu",
    onMenuOpen: () => [
      { label: "Edit", icon: "fa-solid fa-pencil" },
      { label: "Delete", icon: "fa-solid fa-trash" },
    ],
    onMenuItemClick: (label, dataItem) => {
      if (label === "Edit") onEdit(dataItem as UpsertDownloadRecord);
      if (label === "Delete") onDelete(dataItem as UpsertDownloadRecord);
    },
  },
  { field: "id", title: "ID", width: 70, hide: true, pinned: true },
  {
    field: "title",
    title: "Title",
    width: 280,
    renderCell: (data: ValueObject) => (
      <CPText noWrap style={{ textTransform: "capitalize" }}>
        {data.dataItem.title}
      </CPText>
    ),
  },
  {
    field: "fileName",
    title: "File Name",
    width: 220,
    renderCell: (data: ValueObject) => (
      <CPText noWrap>{data.dataItem.fileName}</CPText>
    ),
  },
  {
    field: "downloadURL",
    title: "URL",
    width: 260,
    sortable: false,
    renderCell: (data: ValueObject) => {
      const url: string = data.dataItem.downloadURL;
      if (!url) return null;
      return (
        <Link url={url} target="_blank" style={{ fontSize: 14 }}>
          {url}
        </Link>
      );
    },
  },
  {
    field: "storageProvider",
    title: "Storage",
    width: 130,
    renderCell: (data: ValueObject) => {
      const provider: string = data.dataItem.storageProvider;
      if (!provider) return null;
      const labelMap: Record<string, string> = {
        AZURE_BLOB: "Azure Blob",
        SHAREPOINT: "SharePoint",
        LOCAL: "Local",
      };
      return <Chip text={labelMap[provider] ?? provider} size="small" />;
    },
  },
  {
    field: "isActive",
    title: "Status",
    width: 100,
    renderCell: (data: ValueObject) => (
      <Chip
        text={data.dataItem.isActive ? "Active" : "Inactive"}
        themeColor={data.dataItem.isActive ? "success" : "error"}
        size="small"
      />
    ),
  },
  {
    field: "resourceTier",
    title: "Tier",
    width: 110,
    renderCell: (data: ValueObject) => {
      const tier: string = data.dataItem.resourceTier;
      if (!tier) return null;
      const tierMap: Record<string, { color: string; bg: string }> = {
        BRONZE: { color: "#b5651d", bg: "#fdf3e7" },
        SILVER: { color: "#546e7a", bg: "#eceff1" },
        GOLD: { color: "#b8860b", bg: "#fffde7" },
      };
      const s = tierMap[tier.toUpperCase()] ?? {
        color: "#64748b",
        bg: "#f1f5f9",
      };
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            borderRadius: 10,
            fontSize: "0.73rem",
            fontWeight: 600,
            backgroundColor: s.bg,
            color: s.color,
            border: `1px solid ${s.color}40`,
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: s.color,
              flexShrink: 0,
            }}
          />
          {tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase()}
        </span>
      );
    },
  },
  {
    field: "categories",
    title: "Categories",
    width: 220,
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.categories ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "agencies",
    title: "Agencies",
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.agencies ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "roles",
    title: "Roles",
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.roles ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
];

const AdminDownloadsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { setToastMsg } = useAppState();
  const confirm = useConfirm();
  const { mutate: deleteDownload, isLoading: isDeleting } =
    useDeleteDownloadMutation();
  const [filterParams, setFilterParams] =
    React.useState<UseGetDownloadsForGridQueryOptions>({ take: 20, skip: 0 });
  const { data: categories } = useResourceCategoriesLookupQuery();
  const { data: agencies } = useAgenciesLookupQuery();
  const { data: downloads, isFetching: isDownloadsLoading } =
    useGetDownloadsForGridQuery(filterParams);

  const [selectedDownload, setSelectedDownload] =
    React.useState<UpsertDownloadRecord | null>(null);

  const handleDeleteDownload = (download: UpsertDownloadRecord) => {
    confirm((confirmed) => {
      if (confirmed) {
        deleteDownload(download.id, {
          onSuccess: () => {
            setToastMsg("Download deleted successfully", "success");
            queryClient.invalidateQueries(["downloads-grid"]);
          },
          onError: () => {
            setToastMsg("Failed to delete download", "error");
          },
        });
      }
    }, `Are you sure you want to delete "${download.title}"?`);
  };

  const adapter = useCPGridV2({
    columns: createColumns({
      onEdit: setSelectedDownload,
      onDelete: handleDeleteDownload,
    }),
    data: downloads?.records ?? [],
    showLoader: isDownloadsLoading,
    totalRecords: downloads?.totalRecords ?? 0,
  });

  const filterOptions = React.useMemo<CPGridV2FilterOption[]>(() => {
    return [
      {
        filterType: "text",
        label: "Title",
        columnKey: "title",
        placeholder: "e.g. Annual Report",
      },
      {
        filterType: "dropdown",
        label: "Category",
        columnKey: "categories",
        options:
          categories?.map((category) => ({
            label: category,
            value: category,
          })) ?? [],
      },
      {
        filterType: "multicheck",
        label: "Agency",
        columnKey: "agencies",
        toType: "array",
        options:
          agencies?.map((x) => ({
            label: x.agency,
            value: x.agency,
          })) ?? [],
      },
      {
        filterType: "dropdown",
        label: "Active",
        columnKey: "isActive",
        options: [
          { label: "Active", value: true },
          { label: "Inactive", value: false },
        ],
      },
      {
        filterType: "dropdown",
        label: "Tier",
        columnKey: "resourceTier",
        options: [
          { label: "Bronze", value: "BRONZE" },
          { label: "Silver", value: "SILVER" },
          { label: "Gold", value: "GOLD" },
        ],
      },
    ];
  }, [categories, agencies]);

  return (
    <div className="admin-downloads-page">
      <CPGridV2
        name="admin-downloads-grid"
        title="Download Management"
        adapter={adapter}
        compact
        sortable
        pageable
        noWrap
        gridLines="rows"
        filterOptions={filterOptions}
        onApplyFilters={(filters, paging) => {
          setFilterParams({
            ...filters,
            ...paging,
          } as UseGetDownloadsForGridQueryOptions);
        }}
        onClearFilters={() => setFilterParams({ take: 20, skip: 0 })}
        onRefresh={() => queryClient.invalidateQueries(["downloads-grid"])}
        renderActions={() => (
          <CPButton
            fillMode="flat"
            size="small"
            onClick={() => setSelectedDownload(new UpsertDownloadRecord())}
          >
            + Add Download
          </CPButton>
        )}
      />

      <DownloadDetailsModal
        key={selectedDownload?.id}
        selectedDownload={selectedDownload}
        onClose={() => {
          setSelectedDownload(null);
        }}
        onSave={() => {
          setSelectedDownload(null);
          queryClient.invalidateQueries(["downloads-grid"]);
        }}
        onDelete={() => {
          setSelectedDownload(null);
          queryClient.invalidateQueries(["downloads-grid"]);
        }}
      />
    </div>
  );
};

export { AdminDownloadsPage };
export default AdminDownloadsPage;
